import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

function ApelSemuaDinas() {
  const [loading, setLoading] = useState(false);
  const [dataRekapSemuaDinas, setDataRekapSemuaDinas] = useState(null);
  const [selectedTanggal, setSelectedTanggal] = useState("");
  const [selectedPosisi, setSelectedPosisi] = useState({
    value: "0",
    label: "Di Kota Kabupaten",
  });
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Posisi options
  const posisiOptions = [
    { value: "0", label: "Di Kota Kabupaten" },
    { value: "1", label: "Di Luar Kota Kabupaten" },
  ];

  // Set default tanggal (hari ini dalam WITA)
  useEffect(() => {
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const utc = today.getTime() + today.getTimezoneOffset() * 60000;
    const witaTime = new Date(utc + witaOffset * 60000);
    const todayString = witaTime.toISOString().split("T")[0];
    setSelectedTanggal(todayString);
  }, []);

  // Fetch data rekapan semua dinas
  const fetchRekapSemuaDinas = async () => {
    if (!selectedTanggal) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedPosisi) {
        params.append("posisi", selectedPosisi.value);
      }

      const url = `${API_ESA_OFFICE}apel-harian/rekap-apel-harian/${selectedTanggal}${
        params.toString() ? `?${params.toString()}` : ""
      }`;

      const response = await axios.get(url);
      setDataRekapSemuaDinas(response.data);
    } catch (error) {
      console.error("Error fetching rekap semua dinas:", error);
      setDataRekapSemuaDinas(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data saat tanggal atau posisi berubah
  useEffect(() => {
    fetchRekapSemuaDinas();
  }, [selectedTanggal, selectedPosisi]);

  // Handle tanggal change
  const handleTanggalChange = (event) => {
    setSelectedTanggal(event.target.value);
  };

  // Handle posisi change
  const handlePosisiChange = (event, newValue) => {
    setSelectedPosisi(newValue);
  };

  // Reset filter
  const handleResetFilter = () => {
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const utc = today.getTime() + today.getTimezoneOffset() * 60000;
    const witaTime = new Date(utc + witaOffset * 60000);
    const todayString = witaTime.toISOString().split("T")[0];
    setSelectedTanggal(todayString);
    setSelectedPosisi({ value: "0", label: "Di Kota Kabupaten" });
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!dataRekapSemuaDinas?.data_per_dinas) return;

    const worksheetData = dataRekapSemuaDinas.data_per_dinas.map(
      (dinas, index) => ({
        No: index + 1,
        "Nama Dinas": dinas.nama_dinas,
        Posisi:
          dinas.posisi === 0 ? "Di Kota Kabupaten" : "Di Luar Kota Kabupaten",
        "Total Pegawai": dinas.total_pegawai_dinas,
        "Apel Pagi - Hadir": dinas.apel_pagi.detail_status.hadir,
        "Apel Pagi - Izin": dinas.apel_pagi.detail_status.izin,
        "Apel Pagi - Sakit": dinas.apel_pagi.detail_status.sakit,
        "Apel Pagi - Dinas Luar": dinas.apel_pagi.detail_status.dinas_luar,
        "Apel Pagi - Tanpa Berita": dinas.apel_pagi.detail_status.tanpa_berita,
        "Apel Pagi - Total Tercatat": dinas.apel_pagi.total_tercatat,
        "Apel Pagi - Persentase Kehadiran": `${dinas.apel_pagi.persentase_kehadiran}%`,
        "Apel Siang - Hadir": dinas.apel_siang.detail_status.hadir,
        "Apel Siang - Izin": dinas.apel_siang.detail_status.izin,
        "Apel Siang - Sakit": dinas.apel_siang.detail_status.sakit,
        "Apel Siang - Dinas Luar": dinas.apel_siang.detail_status.dinas_luar,
        "Apel Siang - Tanpa Berita":
          dinas.apel_siang.detail_status.tanpa_berita,
        "Apel Siang - Total Tercatat": dinas.apel_siang.total_tercatat,
        "Apel Siang - Persentase Kehadiran": `${dinas.apel_siang.persentase_kehadiran}%`,
      })
    );

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rekap Apel Semua Dinas");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `Rekap_Apel_Semua_Dinas_${selectedTanggal}${
      selectedPosisi ? `_${selectedPosisi.label.replace(/\s+/g, "_")}` : ""
    }.xlsx`;

    saveAs(data, fileName);
  };

  // Get color for progress bar
  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "success";
    if (percentage >= 60) return "warning";
    return "error";
  };

  // Navigate to detail page for selected dinas
  const handleDetailClick = (row) => {
    const dinasId = row.original?.dinas_id;
    const namaDinas = row.original?.nama_dinas_text || "";
    if (!dinasId) return;
    // Params needed by apelDinasSuperAdmin:
    // - dinas_id: number
    // - tanggal: yyyy-mm-dd (single date)
    // - nama: dinas name (optional, for display only)
    navigate(
      `/pages/apel-harian/apel-dinas-superadmin?dinas_id=${dinasId}&tanggal=${selectedTanggal}&nama=${encodeURIComponent(
        namaDinas
      )}`
    );
  };

  // Columns untuk tabel
  const columns = [
    { Header: "No", accessor: "index", width: "5%" },
    {
      Header: "Nama Dinas",
      accessor: "nama_dinas",
      width: "140px",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            width: "140px",
            wordWrap: "break-word",
            whiteSpace: "normal",
            overflow: "hidden",
            lineHeight: 1.4,
            fontSize: "0.875rem",
          }}
        >
          <MDTypography
            variant="button"
            fontWeight="medium"
            color="text"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.4,
              fontSize: "0.875rem",
            }}
          >
            {value}
          </MDTypography>
        </MDBox>
      ),
    },
    { Header: "Posisi", accessor: "posisi", width: "10%" },
    { Header: "Total Pegawai", accessor: "total_pegawai_dinas", width: "8%" },
    { Header: "Apel Pagi", accessor: "apel_pagi", width: "15%" },
    { Header: "Apel Siang", accessor: "apel_siang", width: "15%" },
    { Header: "Persentase Pagi", accessor: "persentase_pagi", width: "10%" },
    { Header: "Persentase Siang", accessor: "persentase_siang", width: "12%" },
    {
      Header: "Detail",
      accessor: "detail",
      width: "8%",
      Cell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => handleDetailClick(row)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          <Icon sx={{ fontSize: 16 }}>visibility</Icon>
        </MDButton>
      ),
    },
  ];

  // Rows untuk tabel
  const rows = useMemo(() => {
    if (!dataRekapSemuaDinas?.data_per_dinas) return [];

    // Sort by apel pagi persentase kehadiran (highest to lowest)
    const sortedData = [...dataRekapSemuaDinas.data_per_dinas].sort(
      (a, b) =>
        b.apel_pagi.persentase_kehadiran - a.apel_pagi.persentase_kehadiran
    );

    return sortedData.map((dinas, index) => ({
      index: index + 1,
      dinas_id: dinas.dinas_id,
      nama_dinas_text: dinas.nama_dinas,
      nama_dinas: (
        <MDBox>
          <MDTypography
            variant="caption"
            color="text"
            fontWeight="medium"
            sx={{
              lineHeight: 1.2,
              wordBreak: "break-word",
              whiteSpace: "normal",
              display: "block",
            }}
          >
            {dinas.nama_dinas}
          </MDTypography>
        </MDBox>
      ),
      posisi: (
        <Chip
          label={dinas.posisi === 0 ? "Di Kota" : "Di Luar Kota"}
          color={dinas.posisi === 0 ? "success" : "info"}
          size="small"
        />
      ),
      total_pegawai_dinas: dinas.total_pegawai_dinas,
      apel_pagi: (
        <MDBox>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Hadir: {dinas.apel_pagi.detail_status.hadir} | Izin:{" "}
            {dinas.apel_pagi.detail_status.izin}
          </MDTypography>
          <br />
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Sakit: {dinas.apel_pagi.detail_status.sakit} | Dinas Luar:{" "}
            {dinas.apel_pagi.detail_status.dinas_luar}
          </MDTypography>
          <br />
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Tanpa Berita: {dinas.apel_pagi.detail_status.tanpa_berita}
          </MDTypography>
        </MDBox>
      ),
      apel_siang: (
        <MDBox>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Hadir: {dinas.apel_siang.detail_status.hadir} | Izin:{" "}
            {dinas.apel_siang.detail_status.izin}
          </MDTypography>
          <br />
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Sakit: {dinas.apel_siang.detail_status.sakit} | Dinas Luar:{" "}
            {dinas.apel_siang.detail_status.dinas_luar}
          </MDTypography>
          <br />
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Tanpa Berita: {dinas.apel_siang.detail_status.tanpa_berita}
          </MDTypography>
        </MDBox>
      ),
      persentase_pagi: (
        <MDBox>
          <MDProgress
            variant="gradient"
            color={getProgressColor(dinas.apel_pagi.persentase_kehadiran)}
            value={dinas.apel_pagi.persentase_kehadiran}
            label={`${dinas.apel_pagi.persentase_kehadiran}%`}
          />
        </MDBox>
      ),
      persentase_siang: (
        <MDBox>
          <MDProgress
            variant="gradient"
            color={getProgressColor(dinas.apel_siang.persentase_kehadiran)}
            value={dinas.apel_siang.persentase_kehadiran}
            label={`${dinas.apel_siang.persentase_kehadiran}%`}
          />
        </MDBox>
      ),
    }));
  }, [dataRekapSemuaDinas?.data_per_dinas]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          {/* Filter Card */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography
                  variant="h6"
                  color="text"
                  fontWeight="medium"
                  mb={2}
                >
                  Filter Rekapan Apel Semua Dinas
                </MDTypography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <MDInput
                      type="date"
                      label="Pilih Tanggal"
                      value={selectedTanggal}
                      onChange={handleTanggalChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={posisiOptions}
                      value={selectedPosisi}
                      onChange={handlePosisiChange}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Pilih Posisi Dinas"
                          placeholder="Semua posisi"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <MDBox>
                            <MDTypography variant="body2" color="text">
                              {option.label}
                            </MDTypography>
                          </MDBox>
                        </li>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={handleResetFilter}
                        disabled={loading}
                      >
                        <Icon>refresh</Icon>
                        &nbsp;Reset
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Statistics Card */}
          {dataRekapSemuaDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={2}>
                      <MDBox textAlign="center">
                        <MDTypography
                          variant="h4"
                          color="info"
                          fontWeight="bold"
                        >
                          {dataRekapSemuaDinas.total_keseluruhan.total_dinas}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          Total Dinas
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <MDBox textAlign="center">
                        <MDTypography
                          variant="h4"
                          color="success"
                          fontWeight="bold"
                        >
                          {
                            dataRekapSemuaDinas.total_keseluruhan
                              .total_pegawai_semua_dinas
                          }
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          Total Pegawai
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <MDBox textAlign="center">
                        <MDTypography
                          variant="h4"
                          color="primary"
                          fontWeight="bold"
                        >
                          {
                            dataRekapSemuaDinas.total_keseluruhan.apel_pagi
                              .total_hadir_semua_dinas
                          }
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          Hadir Apel Pagi
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <MDBox textAlign="center">
                        <MDTypography
                          variant="h4"
                          color="secondary"
                          fontWeight="bold"
                        >
                          {
                            dataRekapSemuaDinas.total_keseluruhan.apel_siang
                              .total_hadir_semua_dinas
                          }
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          Hadir Apel Siang
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <MDBox textAlign="center">
                        <MDTypography
                          variant="h4"
                          color="warning"
                          fontWeight="bold"
                        >
                          {
                            dataRekapSemuaDinas.total_keseluruhan.apel_pagi
                              .persentase_kehadiran_keseluruhan
                          }
                          %
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          Persentase Pagi
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <MDBox textAlign="center">
                        <MDTypography
                          variant="h4"
                          color="error"
                          fontWeight="bold"
                        >
                          {
                            dataRekapSemuaDinas.total_keseluruhan.apel_siang
                              .persentase_kehadiran_keseluruhan
                          }
                          %
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                        >
                          Persentase Siang
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  </Grid>
                  <MDBox mt={2}>
                    <MDTypography variant="body2" color="text">
                      <strong>Filter:</strong>{" "}
                      {dataRekapSemuaDinas.filter.posisi_keterangan} |
                      <strong> Tanggal:</strong>{" "}
                      {new Date(dataRekapSemuaDinas.tanggal).toLocaleDateString(
                        "id-ID",
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }
                      )}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Data Table */}
          {dataRekapSemuaDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={3}
                >
                  <MDBox>
                    <MDTypography variant="h6" color="text">
                      Data Rekapan Apel Semua Dinas
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Total: {dataRekapSemuaDinas.data_per_dinas.length} dinas
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={handleExportExcel}
                    disabled={loading}
                  >
                    <Icon>download</Icon>
                    &nbsp;Export Excel
                  </MDButton>
                </MDBox>
                {isMobile ? (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    {loading && (
                      <MDTypography variant="button" color="text.secondary">
                        Memuat...
                      </MDTypography>
                    )}
                    {!loading &&
                      [...(dataRekapSemuaDinas?.data_per_dinas || [])]
                        .sort(
                          (a, b) =>
                            b.apel_pagi.persentase_kehadiran -
                            a.apel_pagi.persentase_kehadiran
                        )
                        .map((dinas, index) => (
                          <Card key={`${dinas.dinas_id || "row"}-${index}`}>
                            <CardContent>
                              <MDTypography variant="button" fontWeight="bold">
                                {index + 1}. {dinas.nama_dinas}
                              </MDTypography>
                              <MDBox
                                mt={1}
                                display="flex"
                                flexWrap="wrap"
                                gap={0.5}
                              >
                                <Chip
                                  label={
                                    dinas.posisi === 0
                                      ? "Di Kota"
                                      : "Di Luar Kota"
                                  }
                                  color={
                                    dinas.posisi === 0 ? "success" : "info"
                                  }
                                  size="small"
                                />
                                <Chip
                                  label={`Pegawai: ${dinas.total_pegawai_dinas}`}
                                  color="info"
                                  size="small"
                                />
                              </MDBox>
                              <MDBox mt={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Apel Pagi
                                </MDTypography>
                                <MDBox
                                  mt={0.5}
                                  display="flex"
                                  flexWrap="wrap"
                                  gap={0.5}
                                >
                                  <Chip
                                    label={`Hadir: ${dinas.apel_pagi.detail_status.hadir}`}
                                    color="success"
                                    size="small"
                                  />
                                  <Chip
                                    label={`Izin: ${dinas.apel_pagi.detail_status.izin}`}
                                    color="warning"
                                    size="small"
                                  />
                                  <Chip
                                    label={`Sakit: ${dinas.apel_pagi.detail_status.sakit}`}
                                    color="error"
                                    size="small"
                                  />
                                  <Chip
                                    label={`Dinas Luar: ${dinas.apel_pagi.detail_status.dinas_luar}`}
                                    color="secondary"
                                    size="small"
                                  />
                                  <Chip
                                    label={`Tanpa Berita: ${dinas.apel_pagi.detail_status.tanpa_berita}`}
                                    color="default"
                                    size="small"
                                  />
                                </MDBox>
                              </MDBox>
                              <MDBox mt={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Apel Siang
                                </MDTypography>
                                <MDBox
                                  mt={0.5}
                                  display="flex"
                                  flexWrap="wrap"
                                  gap={0.5}
                                >
                                  <Chip
                                    label={`Hadir: ${dinas.apel_siang.detail_status.hadir}`}
                                    color="success"
                                    size="small"
                                  />
                                  <Chip
                                    label={`Izin: ${dinas.apel_siang.detail_status.izin}`}
                                    color="warning"
                                    size="small"
                                  />
                                  <Chip
                                    label={`Sakit: ${dinas.apel_siang.detail_status.sakit}`}
                                    color="error"
                                    size="small"
                                  />
                                  <Chip
                                    label={`Dinas Luar: ${dinas.apel_siang.detail_status.dinas_luar}`}
                                    color="secondary"
                                    size="small"
                                  />
                                  <Chip
                                    label={`Tanpa Berita: ${dinas.apel_siang.detail_status.tanpa_berita}`}
                                    color="default"
                                    size="small"
                                  />
                                </MDBox>
                              </MDBox>
                              <MDBox mt={1}>
                                <MDTypography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Persentase Kepatuhan
                                </MDTypography>
                                <MDBox mt={0.5}>
                                  <MDBox mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                      fontWeight="medium"
                                    >
                                      Apel Pagi:{" "}
                                      {dinas.apel_pagi.persentase_kehadiran}%
                                    </MDTypography>
                                    <MDProgress
                                      variant="gradient"
                                      color={getProgressColor(
                                        dinas.apel_pagi.persentase_kehadiran
                                      )}
                                      value={
                                        dinas.apel_pagi.persentase_kehadiran
                                      }
                                      sx={{ height: 8, borderRadius: 1 }}
                                    />
                                  </MDBox>
                                  <MDBox>
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                      fontWeight="medium"
                                    >
                                      Apel Siang:{" "}
                                      {dinas.apel_siang.persentase_kehadiran}%
                                    </MDTypography>
                                    <MDProgress
                                      variant="gradient"
                                      color={getProgressColor(
                                        dinas.apel_siang.persentase_kehadiran
                                      )}
                                      value={
                                        dinas.apel_siang.persentase_kehadiran
                                      }
                                      sx={{ height: 8, borderRadius: 1 }}
                                    />
                                  </MDBox>
                                </MDBox>
                              </MDBox>
                              <MDBox mt={1}>
                                <MDButton
                                  variant="gradient"
                                  color="info"
                                  size="small"
                                  onClick={() =>
                                    handleDetailClick({ original: dinas })
                                  }
                                  sx={{ minWidth: "auto", px: 1 }}
                                >
                                  <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                                  &nbsp;Detail
                                </MDButton>
                              </MDBox>
                            </CardContent>
                          </Card>
                        ))}
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    loading={loading}
                  />
                )}
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data rekapan apel semua dinas...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!dataRekapSemuaDinas && !loading && selectedTanggal && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data rekapan apel
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih tanggal atau posisi lain
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ApelSemuaDinas;
